import {
  otpustitArendu,
  perehvatitArendu,
  pulsArendy,
  razobratOtkaz,
  sostoyanieResursa,
  vzyatArendu,
  type Arenda,
} from './api';

/**
 * РЕЕСТР АРЕНД ВКЛАДКИ (`s719`, слова владельца на общей приёмке 06.10.2026: «один редактор
 * лида, записи и диалога; аренда при открытии, второй только читает и видит имя редактора»).
 *
 * Одна запись на ресурс («вид:номер») на всю вкладку. Карточку могут открыть сразу несколько
 * мест одного экрана (карточка лида и переписка в ней) — они читают одно состояние, аренда
 * берётся один раз и отпускается, когда закрылось последнее.
 *
 * ⛔ ЗАЩИЩАЕТ СЕРВЕР. Реестр только показывает и вовремя берёт: чужой ход остановит страж,
 * даже если экран отстал или его обошли.
 *
 * Правила:
 *  · открыли карточку — берём аренду; занято — читаем и видим, кто редактирует и с какого времени;
 *  · пока редактирует другой — раз в несколько секунд спрашиваем состояние; освободилось и
 *    человек сейчас за экраном — берём сами; отошёл — ждём кнопку «Редактировать»;
 *  · своя аренда: пульс вкладки («жива») и отметка действий человека; за минуту до истечения —
 *    предупреждение; истекла, перехвачена, утрачена — чтение, вернуть можно только кнопкой;
 *  · черновик экрана реестр не трогает никогда.
 */
export interface SnimokArendy {
  arenda: Arenda | null;
  /** Часы вкладки в миг получения `arenda`: отсчёт идёт от времени сервера, а не от этих часов. */
  poluchena: number;
  /** Слово и код последнего отказа двери аренды или стража. */
  otkaz: { kod: string | null; slovo: string | null } | null;
  idyot: boolean;
  /** Сервер аренду знает. `false` — дверей аренды нет (прежний сервер): экран работает, как раньше. */
  est: boolean;
  /** Аренду вернёт только кнопка: истекла, перехвачена или утрачена — сама не берётся. */
  zhdyotRuki: boolean;
  /** Растёт раз в секунду, пока идёт отсчёт до истечения, — чтобы экран перерисовал остаток. */
  tik: number;
}

interface Zapis {
  tip: string;
  id: number;
  /** Сколько мест экрана открыли карточку. */
  otkryli: number;
  /** Сколько из них хотят редактировать (`brat`). */
  berut: number;
  snimok: SnimokArendy;
  slushateli: Set<() => void>;
  aktivnost: boolean;
  pulsBylo: number;
  oprosBylo: number;
  zanyato: boolean;
  ozhidanie: Promise<boolean> | null;
}

const PUSTO: SnimokArendy = { arenda: null, poluchena: 0, otkaz: null, idyot: false, est: true, zhdyotRuki: false, tik: 0 };

/** Как часто читающий спрашивает, не освободилось ли. */
const OPROS_MS = 5_000;
/** Человек «за экраном», если действовал во вкладке не дольше этого назад. */
const ZA_EKRANOM_MS = 60_000;
/** В окне предупреждения действие человека продлевает аренду сразу, но не чаще этого. */
const SROCHNYY_PULS_MS = 3_000;

const zapisi = new Map<string, Zapis>();
/** Разовые ходы без открытой карточки (перетаскивание на доске): сколько их сейчас по ресурсу. */
const razovye = new Map<string, number>();
let razovyeBerut = 0;
let taymer: number | undefined;
let poslednyayaAktivnost = 0;
let slushaem = false;
let smenaUchetki: symbol | null = null;

const klyuchResursa = (tip: string, id: number) => `${tip}:${id}`;

function izmenit(z: Zapis, chast: Partial<SnimokArendy>): void {
  z.snimok = { ...z.snimok, ...chast };
  for (const s of z.slushateli) s();
}

function moya(a: Arenda | null): boolean {
  return a !== null && a.zanyata && a.moya;
}

/** Принять состояние аренды с сервера; более старый ответ свежего не затирает. */
function prinyat(z: Zapis, a: Arenda | null | undefined, chast: Partial<SnimokArendy> = {}): void {
  if (a == null) {
    if (Object.keys(chast).length > 0) izmenit(z, chast);

    return;
  }

  const bylo = z.snimok.arenda;
  if (bylo !== null && Date.parse(a.server_vremya) < Date.parse(bylo.server_vremya)) {
    if (Object.keys(chast).length > 0) izmenit(z, chast);

    return;
  }

  // Была моей и перестала: вернуть её может только рука человека.
  const poteryal = moya(bylo) && !moya(a);
  izmenit(z, {
    arenda: a,
    poluchena: Date.now(),
    est: true,
    ...(poteryal ? { zhdyotRuki: true } : {}),
    ...(moya(a) ? { zhdyotRuki: false } : {}),
    ...chast,
  });
}

function hod(z: Zapis, deystvie: () => Promise<{ arenda: Arenda }>): Promise<boolean> {
  if (z.zanyato || smenaUchetki) return Promise.resolve(false);
  const ozhidanie = ispolnitHod(z, deystvie);
  z.ozhidanie = ozhidanie;
  void ozhidanie.then(() => {
    if (z.ozhidanie === ozhidanie) z.ozhidanie = null;
  });

  return ozhidanie;
}

async function ispolnitHod(z: Zapis, deystvie: () => Promise<{ arenda: Arenda }>): Promise<boolean> {
  if (z.zanyato) return false;
  z.zanyato = true;
  izmenit(z, { idyot: true });

  try {
    const otvet = await deystvie();
    prinyat(z, otvet.arenda, { otkaz: null, idyot: false });

    return true;
  } catch (e: unknown) {
    const o = razobratOtkaz(e);

    if (o.status === 404 && o.kod === null && o.arenda === null && z.snimok.arenda === null) {
      // Дверей аренды нет вовсе (прежний сервер) — экран работает без блокировок, как раньше.
      izmenit(z, { est: false, idyot: false });
    } else if (o.status === 0) {
      // Запрос не дошёл: состояние не меняем, повторим следующим тактом.
      izmenit(z, { idyot: false });
    } else {
      prinyat(z, o.arenda, { otkaz: { kod: o.kod, slovo: o.message }, idyot: false });
    }

    return false;
  } finally {
    z.zanyato = false;
  }
}

function zaEkranom(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible' && Date.now() - poslednyayaAktivnost <= ZA_EKRANOM_MS;
}

function ostalosSek(s: SnimokArendy): number | null {
  const a = s.arenda;
  if (!moya(a) || a === null || !a.istekaet_at) return null;

  const proshlo = Date.now() - s.poluchena;

  return Math.max(0, Math.round((Date.parse(a.istekaet_at) - Date.parse(a.server_vremya) - proshlo) / 1000));
}

/** Секунд до истечения своей аренды; `null` — аренда не моя. */
export function ostalos(s: SnimokArendy): number | null {
  return ostalosSek(s);
}

function takt(): void {
  if (smenaUchetki) return;
  const now = Date.now();

  for (const z of zapisi.values()) {
    const s = z.snimok;
    if (!s.est || z.zanyato) continue;

    const a = s.arenda;

    if (moya(a) && a !== null) {
      if (z.berut === 0) continue;

      const ost = ostalosSek(s);
      if (ost !== null && ost <= a.pravila.preduprezhdenie_sek + 1) izmenit(z, { tik: s.tik + 1 });

      if (now - z.pulsBylo >= a.pravila.puls_sek * 1000) {
        z.pulsBylo = now;
        const bylo = z.aktivnost;
        z.aktivnost = false;
        void hod(z, () => pulsArendy(z.tip, z.id, bylo));
      }

      continue;
    }

    if (now - z.oprosBylo < OPROS_MS) continue;
    z.oprosBylo = now;

    // Свободно, человек хотел редактировать и сейчас за экраном — берём; иначе только узнаём состояние.
    const svobodno = a !== null && !a.zanyata;
    if (svobodno && z.berut > 0 && !s.zhdyotRuki && zaEkranom()) {
      void hod(z, () => vzyatArendu(z.tip, z.id));
    } else {
      void sostoyanieResursa(z.tip, z.id)
        .then((o) => prinyat(z, o.arenda))
        .catch(() => undefined);
    }
  }
}

function deystvieCheloveka(): void {
  const now = Date.now();
  poslednyayaAktivnost = now;

  for (const z of zapisi.values()) {
    if (z.berut === 0) continue;
    z.aktivnost = true;

    // До истечения меньше порога предупреждения: продлеваем сразу, а не ждём следующего пульса.
    const a = z.snimok.arenda;
    const ost = ostalosSek(z.snimok);
    if (a !== null && ost !== null && ost <= a.pravila.preduprezhdenie_sek && now - z.pulsBylo >= SROCHNYY_PULS_MS && !z.zanyato) {
      z.pulsBylo = now;
      z.aktivnost = false;
      void hod(z, () => pulsArendy(z.tip, z.id, true));
    }
  }
}

function otpustitVsyo(): void {
  for (const z of zapisi.values()) {
    if (z.berut > 0 && moya(z.snimok.arenda)) otpustitArendu(z.tip, z.id);
  }
}

/**
 * Отпустить все аренды вкладки ДО смены пользователя (вход под учёткой, возврат, выход): после
 * смены сессия уже чужая, и отпустить аренду прежнего человека ей не дадут.
 */
export async function otpustitVseArendy(): Promise<() => void> {
  if (smenaUchetki || razovye.size > 0 || razovyeBerut > 0) throw new Error('lease_operation_pending');
  const vlastnik = Symbol('session_change');
  smenaUchetki = vlastnik;
  const zavershit = () => {
    if (smenaUchetki === vlastnik) smenaUchetki = null;
  };
  try {
    // Начатое получение/продление может вернуть нашу аренду уже после нажатия «Войти как».
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        Promise.all([...zapisi.values()].map((z) => z.ozhidanie)),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('lease_wait_timeout')), 5000);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
    const rezultaty = await Promise.allSettled([...zapisi.values()]
      .filter((z) => z.berut > 0 && moya(z.snimok.arenda))
      .map(async (z) => {
        await otpustitArendu(z.tip, z.id, true);
        izmenit(z, { arenda: null, zhdyotRuki: true });
      }));
    const otkaz = rezultaty.find((r) => r.status === 'rejected');
    if (otkaz?.status === 'rejected') throw otkaz.reason;
    // Возвращаем владельцу операции барьер, а не снимаем его до auth POST/DELETE.
    return zavershit;
  } catch (error) {
    zavershit();
    throw error;
  }
}

function slushat(): void {
  if (slushaem || typeof window === 'undefined') return;
  slushaem = true;
  window.addEventListener('keydown', deystvieCheloveka, true);
  window.addEventListener('pointerdown', deystvieCheloveka, true);
  window.addEventListener('pagehide', otpustitVsyo);
  taymer = window.setInterval(takt, 1000);
}

function neSlushat(): void {
  if (!slushaem || zapisi.size > 0) return;
  slushaem = false;
  window.removeEventListener('keydown', deystvieCheloveka, true);
  window.removeEventListener('pointerdown', deystvieCheloveka, true);
  window.removeEventListener('pagehide', otpustitVsyo);
  window.clearInterval(taymer);
  taymer = undefined;
}

/**
 * Открыть карточку ресурса. `brat` — человек будет редактировать (аренда берётся сразу);
 * `false` — только видеть, кто редактирует. Возвращает «закрыть».
 */
export function otkrytResurs(tip: string, id: number, brat: boolean, sServera?: Arenda | null): () => void {
  const k = klyuchResursa(tip, id);
  let z = zapisi.get(k);

  if (z === undefined) {
    z = { tip, id, otkryli: 0, berut: 0, snimok: PUSTO, slushateli: new Set(), aktivnost: false, pulsBylo: Date.now(), oprosBylo: Date.now(), zanyato: false, ozhidanie: null };
    zapisi.set(k, z);
  }

  const zapis = z;
  zapis.otkryli += 1;
  if (sServera != null) prinyat(zapis, sServera);

  // Открытие карточки — действие человека.
  poslednyayaAktivnost = Date.now();
  slushat();

  if (brat) {
    zapis.berut += 1;
    if (zapis.berut === 1) {
      /*
       * Берём ВСЕГДА, даже когда ответ карточки уже говорит «редактируете вы» (карточка открыта в
       * другом окне этого же человека): сервер обязан узнать и об ЭТОМ окне. Иначе закрытие
       * первого окна в ближайшие секунды отпустило бы блокировку, и это окно осталось бы
       * редактором без блокировки до первого пульса.
       */
      zapis.snimok = { ...zapis.snimok, zhdyotRuki: false };
      void hod(zapis, () => vzyatArendu(tip, id));
    }
  } else if (zapis.otkryli === 1 && sServera == null) {
    void sostoyanieResursa(tip, id)
      .then((o) => prinyat(zapis, o.arenda))
      .catch(() => undefined);
  }

  let zakryto = false;

  return () => {
    if (zakryto) return;
    zakryto = true;
    zapis.otkryli -= 1;

    if (brat) {
      zapis.berut -= 1;
      if (zapis.berut === 0 && moya(zapis.snimok.arenda) && (razovye.get(k) ?? 0) === 0) {
        otpustitArendu(tip, id);
        // Своё состояние забываем: следующий открывший спросит сервер заново.
        zapis.snimok = { ...zapis.snimok, arenda: null };
      }
    }

    if (zapis.otkryli <= 0) {
      zapisi.delete(k);
      neSlushat();
    }
  };
}

export function podpisatsya(tip: string, id: number, slushatel: () => void): () => void {
  const z = zapisi.get(klyuchResursa(tip, id));
  if (z === undefined) return () => undefined;
  z.slushateli.add(slushatel);

  return () => {
    z.slushateli.delete(slushatel);
  };
}

export function snimok(tip: string, id: number): SnimokArendy {
  return zapisi.get(klyuchResursa(tip, id))?.snimok ?? PUSTO;
}

/** «Продолжить» и «Редактировать»: жива — продлеваем действием, иначе берём заново. */
export async function prodolzhit(tip: string, id: number): Promise<void> {
  const z = zapisi.get(klyuchResursa(tip, id));
  if (z === undefined) return;

  z.aktivnost = false;
  z.pulsBylo = Date.now();
  poslednyayaAktivnost = Date.now();

  const a = z.snimok.arenda;
  // Аренда ещё числится за мной (жива или истекла, но никем не взята) — пульс с действием её оживит.
  const pulsom = a !== null && (moya(a) || a.istekla) && (await hod(z, () => pulsArendy(tip, id, true)));
  if (!pulsom) await hod(z, () => vzyatArendu(tip, id));
}

export async function perehvatit(tip: string, id: number): Promise<void> {
  const z = zapisi.get(klyuchResursa(tip, id));
  if (z !== undefined) await hod(z, () => perehvatitArendu(tip, id));
}

export function otmetitAktivnost(tip: string, id: number): void {
  const z = zapisi.get(klyuchResursa(tip, id));
  if (z !== undefined) z.aktivnost = true;
  poslednyayaAktivnost = Date.now();
}

/**
 * Любой ответ сервера, несущий состояние аренды ресурса (разбор лида, отказ стража), доезжает
 * до открытой карточки сразу: отказ хода переводит её в чтение без ожидания следующего опроса.
 */
export function prinyatOtvetServera(resurs: string, telo: unknown, otkaz: boolean): void {
  const z = zapisi.get(resurs);
  if (z === undefined || telo === null || typeof telo !== 'object') return;

  const t = telo as { arenda?: Arenda | null; kod?: unknown; message?: unknown };
  if (t.arenda == null || typeof t.arenda !== 'object' || typeof t.arenda.server_vremya !== 'string') return;

  const kod = typeof t.kod === 'string' ? t.kod : null;
  const ohArende = otkaz && kod !== null && kod.startsWith('arenda.');

  prinyat(z, t.arenda, ohArende ? { otkaz: { kod, slovo: typeof t.message === 'string' ? t.message : null } } : {});
}

/** Карточка ресурса открыта на редактирование в этой вкладке — аренду ведёт она, а не разовый ход. */
export function vedyotKartochka(resurs: string): boolean {
  return (zapisi.get(resurs)?.berut ?? 0) > 0;
}

/** Карточка ресурса открыта (в любом режиме): экран перечитывает ресурс, его версия свежая. */
export function kartochkaOtkryta(resurs: string): boolean {
  return zapisi.has(resurs);
}

/**
 * РАЗОВЫЙ ХОД без открытой карточки (перетаскивание на доске, кнопка в списке): аренда берётся
 * на время запроса и отпускается после ответа. Возвращает версию ресурса на миг взятия и
 * «отпустить»; `null` — аренду ведёт открытая карточка либо дверей аренды нет.
 * Отказ двери (занято другим) пробрасывается: ход не отправляется, человек читает слово сервера.
 */
export async function vzyatNaHod(resurs: string): Promise<{ versiya: string | null; otpustit: () => void } | null> {
  if (smenaUchetki) throw new Error('lease_operation_pending');
  if (vedyotKartochka(resurs)) return null;

  const [tip, nomer] = resurs.split(':');
  const id = Number(nomer);
  if (!tip || !Number.isFinite(id)) return null;

  let versiya: string | null;
  razovyeBerut += 1;
  try {
    const otvet = await vzyatArendu(tip, id);
    versiya = typeof otvet.versiya === 'string' ? otvet.versiya : null;
  } catch (e: unknown) {
    const o = razobratOtkaz(e);
    // Двери нет, запрос не дошёл или сбой сервера — ход идёт как раньше, решит страж.
    if (o.kod === null || !o.kod.startsWith('arenda.')) return null;

    throw e;
  } finally {
    razovyeBerut -= 1;
  }

  razovye.set(resurs, (razovye.get(resurs) ?? 0) + 1);
  let otpushcheno = false;

  return {
    versiya,
    otpustit: () => {
      if (otpushcheno) return;
      otpushcheno = true;

      const ostalis = (razovye.get(resurs) ?? 1) - 1;
      if (ostalis > 0) {
        razovye.set(resurs, ostalis);

        return;
      }
      razovye.delete(resurs);
      // Пока шёл ход, карточку могли открыть — тогда аренду ведёт она.
      if (!vedyotKartochka(resurs)) otpustitArendu(tip, id);
    },
  };
}

/**
 * Ход, чей адрес ресурса не называет (номер лида в теле запроса, статус с экрана «Лиды с форм»):
 * экран называет ресурс сам. Аренда берётся на время хода, если её не ведёт открытая карточка;
 * делу передаётся версия на миг взятия — её нужно послать заголовком `X-Versiya`.
 */
export async function hodSArendoy<T>(resurs: string, delo: (versiya: string | null) => Promise<T>): Promise<T> {
  const razovaya = await vzyatNaHod(resurs);

  try {
    return await delo(razovaya?.versiya ?? null);
  } finally {
    razovaya?.otpustit();
  }
}

/** Для проверок. */
export function sbrositReestr(): void {
  smenaUchetki = null;
  razovyeBerut = 0;
  zapisi.clear();
  razovye.clear();
  poslednyayaAktivnost = 0;
  if (typeof window !== 'undefined' && slushaem) {
    slushaem = false;
    window.removeEventListener('keydown', deystvieCheloveka, true);
    window.removeEventListener('pointerdown', deystvieCheloveka, true);
    window.removeEventListener('pagehide', otpustitVsyo);
    window.clearInterval(taymer);
    taymer = undefined;
  }
}
